import { letterIndex, lettersOnly } from "@/lib/tool-kit/ciphers/alphabet";

/**
 * Francis Bacon's biliteral cipher: every letter becomes five symbols drawn from two —
 * "a" and "b" — so a message can hide in anything that comes in two kinds.
 *
 * Bacon's own alphabet (De Augmentis Scientiarum, 1623) has 24 letters: I and J share a
 * code, as do U and V, as in the Latin alphabet of his day. The 26-letter variant gives
 * every modern letter its own code — the two agree up to I and differ from J onwards.
 * In both, a letter's code is its position written in binary, a = 0 and b = 1.
 */

export type BaconAlphabet = "24" | "26";

const ALPHABET_24 = "ABCDEFGHIKLMNOPQRSTUWXYZ";
const ALPHABET_26 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const CODE_LENGTH = 5;
const MERGED_24: Readonly<Record<string, string>> = { J: "I", V: "U" };
const ANY_LETTER = /\p{L}/u;

export function baconLetters(alphabet: BaconAlphabet): string {
  return alphabet === "24" ? ALPHABET_24 : ALPHABET_26;
}

/** A capital A–Z letter's code number, 0-based. */
export function baconValue(letter: string, alphabet: BaconAlphabet): number {
  if (alphabet === "26") {
    return letterIndex(letter);
  }
  return ALPHABET_24.indexOf(MERGED_24[letter] ?? letter);
}

/** A code number as five bits, most significant first: 1 → [0, 0, 0, 0, 1]. */
export function baconBits(value: number): number[] {
  const bits: number[] = [];
  for (let place = CODE_LENGTH - 1; place >= 0; place -= 1) {
    bits.push(Math.floor(value / 2 ** place) % 2);
  }
  return bits;
}

/** The letter a code number stands for; "" when the alphabet has no such letter. */
export function baconLetterFor(value: number, alphabet: BaconAlphabet): string {
  return baconLetters(alphabet).charAt(value);
}

/** The bits of every letter in a message, in order. Non-letters carry no code. */
export function messageBits(text: string, alphabet: BaconAlphabet): number[] {
  return [...lettersOnly(text)].flatMap((letter) =>
    baconBits(baconValue(letter, alphabet))
  );
}

/** Bits written with two symbols, in groups of five when `group` is set. */
export function writeBaconBits(
  bits: readonly number[],
  symbols: readonly [string, string],
  group: boolean
): string {
  let written = "";
  for (const [index, bit] of bits.entries()) {
    if (group && index > 0 && index % CODE_LENGTH === 0) {
      written += " ";
    }
    written += symbols[bit];
  }
  return written;
}

export function baconEncode(
  text: string,
  alphabet: BaconAlphabet,
  symbols: readonly [string, string],
  group: boolean
): string {
  return writeBaconBits(messageBits(text, alphabet), symbols, group);
}

/** How a decoder reads the two kinds out of a text. */
export type BaconReading =
  | { kind: "symbols"; symbols: readonly [string, string] }
  /** Lowercase letters are "a", capitals are "b"; everything else is ignored. */
  | { kind: "case" };

/**
 * The bits in a text. With symbols, a character matching neither (compared without
 * regard to case) is ignored — so spacing, grouping and line breaks never matter.
 */
export function readBaconBits(text: string, reading: BaconReading): number[] {
  const bits: number[] = [];
  if (reading.kind === "case") {
    for (const char of text) {
      if (ANY_LETTER.test(char) && char.toLowerCase() !== char.toUpperCase()) {
        bits.push(char === char.toUpperCase() ? 1 : 0);
      }
    }
    return bits;
  }
  const [a, b] = reading.symbols.map((symbol) => symbol.toLowerCase());
  for (const char of text) {
    const lower = char.toLowerCase();
    if (lower === a) {
      bits.push(0);
    } else if (lower === b) {
      bits.push(1);
    }
  }
  return bits;
}

export interface BaconGroup {
  /** The five bits as a/b, e.g. "aabab". */
  code: string;
  /** The letter, or "" when the code is past the end of the alphabet. */
  letter: string;
}

export interface BaconDecodeResult {
  output: string;
  groups: BaconGroup[];
  /** Groups that stand for no letter (codes 24–31, or 26–31). */
  invalid: number;
  /** Symbols left over after the last complete group of five. */
  leftover: number;
}

export function baconDecode(
  bits: readonly number[],
  alphabet: BaconAlphabet
): BaconDecodeResult {
  const groups: BaconGroup[] = [];
  let output = "";
  let invalid = 0;
  for (
    let start = 0;
    start + CODE_LENGTH <= bits.length;
    start += CODE_LENGTH
  ) {
    const chunk = bits.slice(start, start + CODE_LENGTH);
    const value = chunk.reduce((total, bit) => total * 2 + bit, 0);
    const letter = baconLetterFor(value, alphabet);
    groups.push({
      code: chunk.map((bit) => (bit === 1 ? "b" : "a")).join(""),
      letter,
    });
    if (letter === "") {
      invalid += 1;
      output += "?";
    } else {
      output += letter;
    }
  }
  return { output, groups, invalid, leftover: bits.length % CODE_LENGTH };
}

export interface CoverRun {
  text: string;
  /** Carries a "b": drawn in the second style. */
  marked: boolean;
}

export interface CoverText {
  runs: CoverRun[];
  /** Letters in the cover text — each can carry one symbol. */
  carriers: number;
  /** Symbols the message needs. */
  needed: number;
}

/**
 * Hide bits in a cover text, Bacon's way: each letter of the cover carries one symbol,
 * "a" in the ordinary style and "b" in the other. Spaces and punctuation carry nothing.
 * Letters after the message has run out stay ordinary. Returned as runs of same-style
 * text, ready to render.
 */
export function hideInCover(bits: readonly number[], cover: string): CoverText {
  const runs: CoverRun[] = [];
  let used = 0;
  let carriers = 0;
  for (const char of cover) {
    let marked = false;
    if (ANY_LETTER.test(char)) {
      carriers += 1;
      if (used < bits.length) {
        marked = bits[used] === 1;
        used += 1;
      }
    }
    const last = runs.at(-1);
    if (last && last.marked === marked) {
      last.text += char;
    } else {
      runs.push({ text: char, marked });
    }
  }
  return { runs, carriers, needed: bits.length };
}

/** The second style a cover text's "b" letters are set in. */
export type CoverStyle = "bold" | "italic" | "capitals";

/**
 * A run's text as it appears in that style. With capitals, the case of every letter is
 * the signal — "b" letters capital, all others lower case, including any the writer
 * had capitalised — so the result survives being copied as plain text.
 */
export function styleCoverRun(run: CoverRun, style: CoverStyle): string {
  if (style !== "capitals") {
    return run.text;
  }
  return run.marked ? run.text.toUpperCase() : run.text.toLowerCase();
}

export function coverToText(
  runs: readonly CoverRun[],
  style: CoverStyle
): string {
  return runs.map((run) => styleCoverRun(run, style)).join("");
}

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};
const HTML_SPECIAL = /[&<>"]/g;

function escapeHtml(text: string): string {
  return text.replace(HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

/** The cover text as HTML, for a rich copy that keeps the bold or italic letters. */
export function coverToHtml(
  runs: readonly CoverRun[],
  style: CoverStyle
): string {
  const tag = style === "italic" ? "i" : "b";
  return runs
    .map((run) =>
      run.marked && style !== "capitals"
        ? `<${tag}>${escapeHtml(run.text)}</${tag}>`
        : escapeHtml(styleCoverRun(run, style))
    )
    .join("");
}

/**
 * Two custom symbols, if usable: one visible character each (surrounding spaces are
 * ignored), and different even when case is ignored — the decoder reads symbols without
 * regard to case. Null when they are not.
 */
export function customSymbols(
  a: string,
  b: string
): readonly [string, string] | null {
  const [first, second] = [[...a.trim()], [...b.trim()]];
  if (first.length !== 1 || second.length !== 1) {
    return null;
  }
  if (first[0].toLowerCase() === second[0].toLowerCase()) {
    return null;
  }
  return [first[0], second[0]];
}

export interface BaconRunOptions {
  mode: "encode" | "decode";
  input: string;
  alphabet: BaconAlphabet;
  /** The two symbols, or null when a custom pair is unusable (no output then). */
  symbols: readonly [string, string] | null;
  /** Decoding: read a/b from letter case instead of from the symbols. */
  readCase: boolean;
  /** Encoding: write the code in groups of five. */
  group: boolean;
}

export interface BaconRunResult {
  output: string;
  /** The decode's details; null when encoding or when nothing could be read. */
  decoded: BaconDecodeResult | null;
}

/** One pass of the Bacon widget: encode, or decode by symbols or by letter case. */
export function runBacon(options: BaconRunOptions): BaconRunResult {
  const { mode, input, alphabet, symbols, readCase, group } = options;
  if (mode === "encode") {
    const output = symbols ? baconEncode(input, alphabet, symbols, group) : "";
    return { output, decoded: null };
  }
  let reading: BaconReading | null = null;
  if (readCase) {
    reading = { kind: "case" };
  } else if (symbols) {
    reading = { kind: "symbols", symbols };
  }
  if (!reading) {
    return { output: "", decoded: null };
  }
  const decoded = baconDecode(readBaconBits(input, reading), alphabet);
  return { output: decoded.output, decoded };
}
